import React, { useState, useRef, useEffect } from "react";
import { copilotAsk } from "../../../api";
import { Sparkles, Send, Loader2, Building2, TrendingUp } from "lucide-react";
import { ScoreBadge } from "./bits";

const SUGGESTIONS = [
  "Find me the 20 landlords most likely to instruct us in BA1",
  "Which withdrawn properties should I prioritise?",
  "Why is Margaret Hughes ranked highly?",
  "Show me buyers suitable for Duke Street",
];

const AdminCopilot = () => {
  const [messages, setMessages] = useState([{ role: "ai", text: "Ask me anything about your prospects, buyers and pipeline — in plain English." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const ask = async (q) => {
    const text = (q || input).trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await copilotAsk(text);
      setMessages((m) => [...m, { role: "ai", text: r.answer, kind: r.kind, items: r.items || [] }]);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "Sorry — I couldn't process that just now." }]);
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-copilot">
      <div>
        <h2 className="text-[22px] font-semibold text-[#2b2b2b] flex items-center gap-2"><Sparkles size={20} className="text-[#b8935f]" /> AI Agent Copilot</h2>
        <p className="text-[13px] text-[#8a8a8a] mt-1">Natural-language access to your intelligence — no complex filters needed.</p>
      </div>

      <div className="bg-white border border-[#f0ece5] rounded-2xl flex flex-col h-[560px]">
        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="copilot-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] ${m.role === "user" ? "bg-[#b8935f] text-white" : "bg-[#faf7f2] text-[#2b2b2b]"} rounded-2xl px-4 py-2.5`}>
                <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{m.text}</p>
                {(m.items || []).length > 0 && (
                  <div className="mt-3 space-y-2">
                    {m.items.slice(0, 8).map((it, j) => (
                      <div key={j} className="bg-white border border-[#f0ece5] rounded-lg p-2 flex items-center gap-3" data-testid={`copilot-item-${j}`}>
                        {it.score != null && <ScoreBadge score={it.score} />}
                        <div className="flex-1 min-w-0 text-[#2b2b2b]">
                          <div className="text-[13px] font-medium truncate">{it.owner || it.name || it.title}</div>
                          <div className="text-[12px] text-[#8a8a8a] truncate">
                            {it.pLandlord != null ? `${Math.round(it.pLandlord * 100)}% landlord` : it.match != null ? `${it.match}% match` : it.recommended?.action || ""}
                          </div>
                        </div>
                        {m.kind === "landlords" ? <Building2 size={15} className="text-[#b8935f]" /> : m.kind === "sellers" ? <TrendingUp size={15} className="text-[#b8935f]" /> : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="bg-[#faf7f2] rounded-2xl px-4 py-2.5"><Loader2 size={16} className="animate-spin text-[#8a8a8a]" /></div></div>}
          <div ref={endRef} />
        </div>

        <div className="border-t border-[#f0ece5] p-3">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => ask(s)} data-testid="copilot-suggestion" className="text-[11px] bg-[#faf7f2] hover:bg-[#f0e6d6] text-[#6a6a6a] rounded-full px-2.5 py-1">{s}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} data-testid="copilot-input"
              placeholder="Ask the copilot…" className="flex-1 text-[14px] border border-[#f0ece5] rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#b8935f]" />
            <button onClick={() => ask()} disabled={busy} data-testid="copilot-send" className="bg-[#b8935f] hover:bg-[#a37f4e] text-white rounded-xl px-4 py-2.5 disabled:opacity-60"><Send size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCopilot;
